import { Checkbox, Table, Toast } from '@statsbomb/kitbag-components';
import { useAtom, useSetAtom } from 'jotai';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from 'styled-components';
import { REACT_APP_AUTH_ROLE_OPERATOR } from '../../AuthProvider';
import { messageReviewLibraryEntryActionAtom } from '../../atoms/messages';
import {
  editingOffsetsAtom,
  offsetDataIsUpdatingAtom,
} from '../../atoms/review';
import useApi, { API_ENDPOINTS } from '../../hooks/api';
import { videoReferencePropTypes } from '../../types/videoReference';
import { timestampAsMicroseconds } from '../../utils/time';
import { EditButton } from '../EditButton/EditButton';
import Secured from '../Secured';
import From from '../TimestampSelection/From';
import TimestampSelectionWarnings from '../TimestampSelection/TimestampSelectionWarnings';
import To from '../TimestampSelection/To';

export const VALUE_IDENTIFYING_NOT_SHOWN = -1;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

export function OffsetData({
  alwaysInEditMode,
  editingIsDisabled,
  lastUpdatedBy,
  lastUpdatedByIsHidden,
  periodEnd,
  periodEndNotShown,
  periodStart,
  periodStartNotShown,
  reset,
  setPeriodEnd,
  setPeriodEndNotShown,
  setPeriodStart,
  setPeriodStartNotShown,
  videoReference,
  onSave,
}) {
  const intl = useIntl();
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const {
    put,
    isFetching: isSaving,
    errorMessage,
    clearErrorMessage,
  } = useApi();

  const [editingOffsets, setEditingOffsets] = useAtom(editingOffsetsAtom);
  const [offsetDataIsUpdating, setOffsetDataIsUpdating] = useAtom(
    offsetDataIsUpdatingAtom,
  );
  const setReviewLibraryEntryMessage = useSetAtom(
    messageReviewLibraryEntryActionAtom,
  );

  const FAILURE_MESSAGE = {
    text: intl.formatMessage({
      defaultMessage: 'Video information update failed. Please try again',
      id: 'ffbfd',
    }),
    type: 'error',
  };

  const handleEdit = (editing) => {
    if (!editing) {
      reset();
    }
    setEditingOffsets(editing);
  };
  const handleSave = async () => {
    const { matchId, matchPart, sport, track, version } = videoReference;

    const response = await put(
      `${API_ENDPOINTS.videoService}/${sport}/${matchId}/${matchPart}/${track}/${version}/library-entry`,
      {
        data: {
          period_end: periodEndNotShown
            ? VALUE_IDENTIFYING_NOT_SHOWN
            : timestampAsMicroseconds(periodEnd, { includesHours: true }),
          period_start: periodStartNotShown
            ? VALUE_IDENTIFYING_NOT_SHOWN
            : timestampAsMicroseconds(periodStart, { includesHours: true }),
        },
      },
    );

    if (!response) return;

    onSave();
    setEditingOffsets(false);
  };

  useEffect(() => {
    if (errorMessage) {
      setIsErrorOpen(true);
      setReviewLibraryEntryMessage(FAILURE_MESSAGE);
    }
  }, [errorMessage]);

  useEffect(() => {
    setOffsetDataIsUpdating(isSaving);
  }, [isSaving]);

  useEffect(() => {
    reset();
  }, []);

  const offsetSelectionIsValid =
    (periodStartNotShown && periodEndNotShown) ||
    (periodStartNotShown && periodEnd) ||
    (periodEndNotShown && periodStart) ||
    (periodStart && periodEnd && periodStart < periodEnd);

  const saveDisabled = !offsetSelectionIsValid;

  const LAST_UPDATED_BY = intl.formatMessage({
    defaultMessage: 'Last Updated By',
    id: '7ce1b',
  });
  const UNKNOWN = intl.formatMessage({
    defaultMessage: 'Unknown',
    id: '88183',
  });
  const NOT_IN_VIDEO = intl.formatMessage({
    defaultMessage: 'Not In Video',
    id: '36e26',
  });
  const EDIT_OFFSET_DATA_TOOLTIP = intl.formatMessage({
    defaultMessage: 'Edit Offset Data',
    id: 'dc909',
  });
  const EDIT_OFFSET_DATA_DISABLED_TOOLTIP = intl.formatMessage({
    defaultMessage:
      'Offsets for GSL videos can only be changed via the video alignment process',
    id: '861f1',
  });

  const getDisplayValue = (value, notShown) => {
    if (notShown) {
      return NOT_IN_VIDEO;
    }
    return value || UNKNOWN;
  };
  const periodEndDisplay = getDisplayValue(periodEnd, periodEndNotShown);
  const periodStartDisplay = getDisplayValue(periodStart, periodStartNotShown);

  const showEditFunctions = editingOffsets || alwaysInEditMode;

  const handlePeriodEndNotShownClick = () => {
    if (!periodEndNotShown) {
      setPeriodEnd('');
    }
    setPeriodEndNotShown((currentValue) => !currentValue);
  };
  const handlePeriodStartNotShownClick = () => {
    if (!periodStartNotShown) {
      setPeriodStart('');
    }
    setPeriodStartNotShown((currentValue) => !currentValue);
  };

  const handleClose = () => {
    setIsErrorOpen(false);
    clearErrorMessage();
  };

  return (
    <>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell colSpan={showEditFunctions ? 3 : 2}>
              <Container>
                <FormattedMessage defaultMessage="Offsets" id="ddc73" />
                {!alwaysInEditMode && (
                  <Secured allowed={REACT_APP_AUTH_ROLE_OPERATOR}>
                    <EditButton
                      editing={editingOffsets}
                      editTitle={
                        editingIsDisabled
                          ? EDIT_OFFSET_DATA_DISABLED_TOOLTIP
                          : EDIT_OFFSET_DATA_TOOLTIP
                      }
                      isPending={offsetDataIsUpdating}
                      onEdit={handleEdit}
                      onSave={handleSave}
                      saveDisabled={saveDisabled}
                      disabled={editingIsDisabled}
                    />
                  </Secured>
                )}
              </Container>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {!lastUpdatedByIsHidden && (
            <Table.Row>
              <Table.DataCell $textAlign="left">
                {LAST_UPDATED_BY}
              </Table.DataCell>
              <Table.DataCell $textAlign="left">
                {lastUpdatedBy || '-'}
              </Table.DataCell>
            </Table.Row>
          )}
          <Table.Row>
            <Table.DataCell $textAlign="left">
              <FormattedMessage defaultMessage="Period Start" id="3d83f" />
            </Table.DataCell>
            <Table.DataCell $textAlign="left">
              {showEditFunctions ? (
                <From
                  buttonTitle={intl.formatMessage({
                    defaultMessage: 'Align',
                    id: 'd12fb',
                  })}
                  compact
                  disabled={periodStartNotShown}
                  periodStart={periodStart}
                  setPeriodStart={setPeriodStart}
                  size="tiny"
                />
              ) : (
                periodStartDisplay
              )}
            </Table.DataCell>
            {showEditFunctions && (
              <Table.DataCell $textAlign="left">
                <Checkbox
                  checked={periodStartNotShown}
                  id="kickoff-not-shown"
                  label={NOT_IN_VIDEO}
                  onChange={handlePeriodStartNotShownClick}
                  size="small"
                />
              </Table.DataCell>
            )}
          </Table.Row>
          <Table.Row>
            <Table.DataCell $textAlign="left">
              <FormattedMessage defaultMessage="Period End" id="bc6f8" />
            </Table.DataCell>
            <Table.DataCell $textAlign="left">
              {editingOffsets || alwaysInEditMode ? (
                <To
                  buttonTitle={intl.formatMessage({
                    defaultMessage: 'Align',
                    id: 'd12fb',
                  })}
                  compact
                  disabled={periodEndNotShown}
                  periodEnd={periodEnd}
                  setPeriodEnd={setPeriodEnd}
                  size="tiny"
                />
              ) : (
                periodEndDisplay
              )}
            </Table.DataCell>
            {showEditFunctions && (
              <Table.DataCell $textAlign="left">
                <Checkbox
                  checked={periodEndNotShown}
                  id="period-end-not-shown"
                  label={NOT_IN_VIDEO}
                  onChange={handlePeriodEndNotShownClick}
                  size="small"
                />
              </Table.DataCell>
            )}
          </Table.Row>
        </Table.Body>
      </Table>
      {!periodStartNotShown && !periodEndNotShown && (
        <TimestampSelectionWarnings
          periodStart={periodStart}
          periodEnd={periodEnd}
        />
      )}

      {errorMessage && (
        <Toast type="error" isOpen={isErrorOpen} onClose={handleClose}>
          {errorMessage}
        </Toast>
      )}
    </>
  );
}

OffsetData.propTypes = {
  alwaysInEditMode: PropTypes.bool,
  editingIsDisabled: PropTypes.bool,
  lastUpdatedBy: PropTypes.string,
  lastUpdatedByIsHidden: PropTypes.bool,
  onSave: PropTypes.func,
  periodEnd: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  periodEndNotShown: PropTypes.bool.isRequired,
  periodStart: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  periodStartNotShown: PropTypes.bool.isRequired,
  reset: PropTypes.func,
  setPeriodEnd: PropTypes.func.isRequired,
  setPeriodEndNotShown: PropTypes.func.isRequired,
  setPeriodStart: PropTypes.func.isRequired,
  setPeriodStartNotShown: PropTypes.func.isRequired,
  videoReference: PropTypes.shape(videoReferencePropTypes).isRequired,
};
OffsetData.defaultProps = {
  alwaysInEditMode: false,
  editingIsDisabled: false,
  lastUpdatedBy: '',
  lastUpdatedByIsHidden: false,
  onSave: () => {},
  reset: () => {},
};

export default { OffsetData };
